import { Component, OnInit, OnDestroy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData, ChartType } from 'chart.js';
import { Subject, combineLatest, BehaviorSubject } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  finalize,
} from 'rxjs/operators';

import { LogAnalyticsService } from '../../services/log-analytics.service';
import { OwnershipService } from '../../services/ownership.service';
import { EnvironmentService } from '../../services/environment.service';
import { ChartUrlStateService } from './services/chart-url-state.service';
import { ChartDataTransformService } from './services/chart-data-transform.service';
import {
  UrlState,
  ChartDataPoint,
  GroupByOption,
  FilterCriteria,
} from './models/chart-interfaces';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-alerts-breakdown',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatChipsModule,
    MatToolbarModule,
    MatTabsModule,
    MatTooltipModule,
    MatExpansionModule,
    BaseChartDirective
],
  templateUrl: './alerts-breakdown.component.html',
  styleUrls: ['./alerts-breakdown.component.scss'],
})
export class AlertsBreakdownComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly stateChange$ = new BehaviorSubject<UrlState | null>(null);

  // Loading states
  isLoading = false;
  hasError = false;
  errorMessage = '';

  // Data
  chartDataPoints: ChartDataPoint[] = [];
  filteredDataPoints: ChartDataPoint[] = [];

  // Store ownership data for repository filtering
  private ownershipData: any[] = [];

  // Alert count cache for performance
  private alertCountCache = {
    teams: new Map<string, number>(),
    repositories: new Map<string, number>(),
    alertTypes: new Map<string, number>(),
    locations: new Map<string, number>(),
    pipelineTypes: new Map<string, number>(),
    admins: new Map<string, number>(),
    systems: new Map<string, number>(),
    severities: new Map<string, number>()
  };

  // Current state
  currentState: UrlState = this.getDefaultState();

  // Chart configuration
  chartData: ChartData<any> = { labels: [], datasets: [] };
  chartOptions: ChartConfiguration<any>['options'] = {};
  chartType: ChartType = 'bar';

  // UI options
  groupByOptions: GroupByOption[] = [
    {
      value: 'team',
      label: 'Team',
      description: 'Group alerts by team ownership',
    },
    {
      value: 'repository',
      label: 'Repository',
      description: 'Group alerts by repository',
    },
    {
      value: 'alertType',
      label: 'Alert Type',
      description: 'Group alerts by alert name/type',
    },
    {
      value: 'systems',
      label: 'Systems',
      description: 'Group alerts by business systems',
    },
  ];

  chartTypeOptions = [
    { value: 'bar', label: 'Bar Chart', icon: 'bar_chart' },
    { value: 'line', label: 'Line Chart', icon: 'show_chart' },
    { value: 'pie', label: 'Pie Chart', icon: 'pie_chart' },
    { value: 'doughnut', label: 'Doughnut Chart', icon: 'donut_large' },
    { value: 'polarArea', label: 'Polar Area', icon: 'radar' },
  ];

  timeRangeOptions = [
    { value: 'P7D', label: '7 Days' },
    { value: 'P30D', label: '30 Days' },
    { value: 'P90D', label: '90 Days' },
    { value: 'P180D', label: '6 Months' },
    { value: 'P365D', label: '1 Year' },
  ];

  // Filter options (populated from data)
  availableTeams: string[] = [];
  availableRepositories: string[] = [];
  availableAlertTypes: string[] = [];
  availableLocations: string[] = [];
  availablePipelineTypes: string[] = [];
  availableAdmins: string[] = [];
  availableSystems: string[] = [];
  availableSeverities: string[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly logAnalyticsService: LogAnalyticsService,
    private readonly ownershipService: OwnershipService,
    private readonly environmentService: EnvironmentService,
    private readonly urlStateService: ChartUrlStateService,
    private readonly dataTransformService: ChartDataTransformService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    // Initialize URL state service
    this.urlStateService.initialize(this.route, this.router);

    // Load initial state from URL or use defaults
    this.loadInitialState();

    // Set up state change monitoring
    this.setupStateChangeMonitoring();

    // Load data and create chart
    this.loadDataAndCreateChart();
  }

  private loadInitialState(): void {
    const urlState = this.urlStateService.getStateFromUrl();
    this.currentState = urlState || this.getDefaultState();
    this.stateChange$.next(this.currentState);
  }

  private setupStateChangeMonitoring(): void {
    this.stateChange$
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((state) => {
        if (state) {
          this.urlStateService.updateUrl(state);
          this.updateChartFromState(state);
        }
      });
  }

  private loadDataAndCreateChart(): void {
    this.isLoading = true;
    this.hasError = false;

    this.toastService.notification('Loading alert and ownership data...');

    const environment = this.environmentService.getEnvironment();
    const org = environment.defaultOrg;

    combineLatest([
      this.logAnalyticsService.queryAlertsBreakdownByTeam(),
      this.ownershipService.getRepoOwnershipMaster(org),
      this.ownershipService.getTeamOwnerMinor(org),
    ])
      .pipe(
        takeUntil(this.destroy$),
        switchMap(([alertData, ownershipData, teamData]) => {
          this.isLoading = false;
          // Store ownership data for repository filtering
          this.ownershipData = ownershipData;
          return this.dataTransformService.transformData(
            alertData,
            ownershipData,
            teamData
          );
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: (transformedData) => {
          this.chartDataPoints = transformedData;
          this.populateAlertCountCache();
          this.updateFilterOptions();
          this.updateChartFromState(this.currentState);
        },
        error: (error) => {
          this.toastService.error(
            'Failed to load alert and ownership data. Please try again.'
          );
          this.hasError = true;
          this.errorMessage =
            'Failed to load alert and ownership data. Please try again.';
        },
      });
  }

  private updateFilterOptions(): void {
    this.updateCascadingFilterOptions();
  }

  private updateCascadingFilterOptions(): void {
    // Step 1: Always show all teams (primary filter)
    this.availableTeams = Array.from(
      new Set(this.chartDataPoints.map((d) => d.teamName))
    ).sort((a, b) => a.localeCompare(b));

    // Step 2: Filter repositories by selected teams using ownership data
    if (this.currentState.filters.teams?.length) {
      // Only show repositories owned by selected teams
      this.availableRepositories = Array.from(
        new Set(
          this.ownershipData
            .filter(entry => this.currentState.filters.teams!.includes(entry.team))
            .map(entry => entry.repository)
        )
      ).sort((a, b) => a.localeCompare(b));
    } else {
      // Show all repositories from ownership data
      this.availableRepositories = Array.from(
        new Set(this.ownershipData.map(entry => entry.repository))
      ).sort((a, b) => a.localeCompare(b));
    }

    // Step 3: Filter alert types AND systems by selected repositories
    const filteredByRepo = this.getDataFilteredByTeamsAndRepos();
    this.availableAlertTypes = Array.from(
      new Set(filteredByRepo.map((d) => d.alertName))
    ).sort((a, b) => a.localeCompare(b));

    this.availableSystems = Array.from(
      new Set(
        filteredByRepo
          .flatMap((d) => d.systems || [])
          .filter((s): s is string => Boolean(s))
      )
    ).sort((a, b) => a.localeCompare(b));

    // Step 4: Filter severities by selected alert types
    const filteredByAlerts = this.getDataFilteredByTeamsReposAndAlerts();
    this.availableSeverities = Array.from(
      new Set(
        filteredByAlerts
          .map((d) => d.severity)
          .filter((s): s is string => Boolean(s))
      )
    ).sort((a, b) => a.localeCompare(b));

    // Static filters (not cascading in current requirements)
    this.availableLocations = Array.from(
      new Set(
        this.chartDataPoints
          .map((d) => d.location)
          .filter((l): l is string => Boolean(l))
      )
    ).sort((a, b) => a.localeCompare(b));

    this.availablePipelineTypes = Array.from(
      new Set(
        this.chartDataPoints
          .map((d) => d.pipelineType)
          .filter((p): p is 'Release' | 'Build' | 'YAML' => Boolean(p))
      )
    ).sort((a, b) => a.localeCompare(b));

    this.availableAdmins = Array.from(
      new Set(
        this.chartDataPoints
          .map((d) => d.admin)
          .filter((a): a is string => Boolean(a))
      )
    ).sort((a, b) => a.localeCompare(b));
  }

  // Helper methods for cascading filter data
  private getDataFilteredByTeams(): ChartDataPoint[] {
    if (!this.currentState.filters.teams?.length) {
      return this.chartDataPoints;
    }
    return this.chartDataPoints.filter(point =>
      this.currentState.filters.teams!.includes(point.teamName)
    );
  }

  private getDataFilteredByTeamsAndRepos(): ChartDataPoint[] {
    let filtered = this.getDataFilteredByTeams();

    if (this.currentState.filters.repositories?.length) {
      // Now we have repository-level data, so filter directly by repository
      filtered = filtered.filter(point =>
        point.repository && this.currentState.filters.repositories!.includes(point.repository)
      );
    }

    return filtered;
  }

  private getRepositoriesForTeam(teamName: string): string[] {
    // Get repositories owned by this team from the ownership data
    return this.ownershipData
      .filter(entry => entry.team === teamName)
      .map(entry => entry.repository);
  }

  private getDataFilteredByTeamsReposAndAlerts(): ChartDataPoint[] {
    let filtered = this.getDataFilteredByTeamsAndRepos();

    if (this.currentState.filters.alertTypes?.length) {
      filtered = filtered.filter(point =>
        this.currentState.filters.alertTypes!.includes(point.alertName)
      );
    }

    return filtered;
  }

  private updateChartFromState(state: UrlState): void {
    // Apply filters
    this.filteredDataPoints = this.applyFilters(
      this.chartDataPoints,
      state.filters
    );

    // Group and aggregate data
    const groupedData = this.dataTransformService.groupAndAggregateData(
      this.filteredDataPoints,
      state.groupBy,
      state.aggregateBy
    );

    // Create chart configuration
    this.chartType = state.chartType;
    this.chartData = this.dataTransformService.createChartData(
      groupedData,
      state
    );
    this.chartOptions = this.dataTransformService.createChartOptions(state);
  }

  private applyFilters(
    data: ChartDataPoint[],
    filters: FilterCriteria
  ): ChartDataPoint[] {
    const predicates: Array<(point: ChartDataPoint) => boolean> = [];

    if (filters.teams?.length) {
      predicates.push((p) => filters.teams!.includes(p.teamName));
    }
    if (filters.repositories?.length) {
      predicates.push((p) => !p.repository || filters.repositories!.includes(p.repository));
    }
    if (filters.alertTypes?.length) {
      predicates.push((p) => filters.alertTypes!.includes(p.alertName));
    }
    if (filters.locations?.length) {
      predicates.push((p) => !p.location || filters.locations!.includes(p.location));
    }
    if (filters.pipelineTypes?.length) {
      predicates.push((p) => !p.pipelineType || filters.pipelineTypes!.includes(p.pipelineType));
    }
    if (filters.admins?.length) {
      predicates.push((p) => !p.admin || filters.admins!.includes(p.admin));
    }
    if (filters.systems?.length) {
      predicates.push((p) => !p.systems || filters.systems!.some((s) => p.systems!.includes(s)));
    }
    if (filters.severities?.length) {
      predicates.push((p) => !!p.severity && filters.severities!.includes(p.severity));
    }

    return data.filter((point) => predicates.every((pred) => pred(point)));
  }

  private getDefaultState(): UrlState {
    return {
      groupBy: 'team',
      aggregateBy: 'count',
      chartType: 'bar',
      timeRange: 'P30D',
      filters: {},
      chartOptions: {
        stacked: false,
        showLegend: true,
        orientation: 'vertical',
      },
    };
  }

  // Event handlers
  onGroupByChange(groupBy: string): void {
    this.currentState = { ...this.currentState, groupBy };
    this.stateChange$.next(this.currentState);
  }

  onChartTypeChange(chartType: string): void {
    this.currentState = { ...this.currentState, chartType: chartType as any };
    this.stateChange$.next(this.currentState);
  }

  onTimeRangeChange(timeRange: string): void {
    this.currentState = { ...this.currentState, timeRange };
    this.stateChange$.next(this.currentState);
    // Reload data with new time range
    this.loadDataAndCreateChart();
  }

  onFilterChange(filterType: keyof FilterCriteria, values: string[]): void {
    // Update the current filter
    this.currentState = {
      ...this.currentState,
      filters: {
        ...this.currentState.filters,
        [filterType]: values.length > 0 ? values : undefined,
      },
    };

    // Clear dependent filters when parent filters change
    this.clearDependentFilters(filterType);

    // Update available filter options based on new selections
    this.updateCascadingFilterOptions();

    this.stateChange$.next(this.currentState);
  }

  private clearDependentFilters(changedFilterType: keyof FilterCriteria): void {
    switch (changedFilterType) {
      case 'teams':
        // When teams change, clear repositories and all their dependents
        this.currentState.filters.repositories = undefined;
        this.currentState.filters.alertTypes = undefined;
        this.currentState.filters.systems = undefined;
        this.currentState.filters.severities = undefined;
        break;

      case 'repositories':
        // When repositories change, clear alert types, systems, and severities
        this.currentState.filters.alertTypes = undefined;
        this.currentState.filters.systems = undefined;
        this.currentState.filters.severities = undefined;
        break;

      case 'alertTypes':
        // When alert types change, clear severities
        this.currentState.filters.severities = undefined;
        break;

      // Other filters don't have dependents in our current hierarchy
      case 'systems':
      case 'severities':
      case 'locations':
      case 'pipelineTypes':
      case 'admins':
      default:
        // No dependent filters to clear
        break;
    }
  }

  selectAllForFilter(filterType: keyof FilterCriteria): void {
    let allValues: string[] = [];

    switch (filterType) {
      case 'teams':
        allValues = [...this.availableTeams];
        break;
      case 'repositories':
        allValues = [...this.availableRepositories];
        break;
      case 'alertTypes':
        allValues = [...this.availableAlertTypes];
        break;
      case 'locations':
        allValues = [...this.availableLocations];
        break;
      case 'pipelineTypes':
        allValues = [...this.availablePipelineTypes];
        break;
      case 'admins':
        allValues = [...this.availableAdmins];
        break;
      case 'systems':
        allValues = [...this.availableSystems];
        break;
      case 'severities':
        allValues = [...this.availableSeverities];
        break;
    }

    this.onFilterChange(filterType, allValues);
  }

  clearAllForFilter(filterType: keyof FilterCriteria): void {
    this.onFilterChange(filterType, []);
  }

  onChartOptionChange(option: string, value: any): void {
    this.currentState = {
      ...this.currentState,
      chartOptions: {
        ...this.currentState.chartOptions,
        [option]: value,
      },
    };
    this.stateChange$.next(this.currentState);
  }

  clearAllFilters(): void {
    this.currentState = {
      ...this.currentState,
      filters: {},
    };

    // Update available filter options since all filters are cleared
    this.updateCascadingFilterOptions();

    this.stateChange$.next(this.currentState);
  }

  shareChart(): void {
    const shareableUrl = this.urlStateService.generateShareableUrl(
      this.currentState
    );
    navigator.clipboard.writeText(shareableUrl).then(() => {
      // Could add toast notification here
      this.toastService.success('Shareable URL copied to clipboard!');
    });
  }

  retryLoad(): void {
    this.loadDataAndCreateChart();
  }

  // Helper methods for template
  hasActiveFilters(): boolean {
    const filters = this.currentState.filters;
    return !!(
      filters.teams?.length ||
      filters.repositories?.length ||
      filters.alertTypes?.length ||
      filters.locations?.length ||
      filters.pipelineTypes?.length ||
      filters.admins?.length ||
      filters.systems?.length ||
      filters.severities?.length
    );
  }

  getGroupByLabel(groupBy: string): string {
    const option = this.groupByOptions.find((opt) => opt.value === groupBy);
    return option?.label || groupBy;
  }

  getChartTypeLabel(chartType: string): string {
    const option = this.chartTypeOptions.find((opt) => opt.value === chartType);
    return option?.label || chartType;
  }

  getTotalAlerts(): number {
    return this.filteredDataPoints.reduce(
      (total, point) => total + point.alertCount,
      0
    );
  }

  getUniqueAlertTypes(): number {
    return new Set(this.filteredDataPoints.map((point) => point.alertName))
      .size;
  }

  getUniqueTeams(): number {
    return new Set(this.filteredDataPoints.map((point) => point.teamName)).size;
  }

  getUniqueRepositories(): number {
    return new Set(
      this.filteredDataPoints.map((point) => point.repository).filter(Boolean)
    ).size;
  }

  // Dynamic methods to get alert counts for filter options based on current filter context
  getAlertCountForTeam(teamName: string): number {
    // Teams are primary filter, so use cached count (no filtering context)
    return this.alertCountCache.teams.get(teamName) || 0;
  }

  getAlertCountForRepository(repository: string): number {
    // Repository counts should be based on selected teams (if any)
    const filteredData = this.getDataFilteredByTeams();
    return filteredData
      .filter(point => point.repository === repository)
      .reduce((sum, point) => sum + point.alertCount, 0);
  }

  getAlertCountForAlertType(alertType: string): number {
    // Alert type counts should be based on selected teams + repositories
    const filteredData = this.getDataFilteredByTeamsAndRepos();
    return filteredData
      .filter(point => point.alertName === alertType)
      .reduce((sum, point) => sum + point.alertCount, 0);
  }

  getAlertCountForSystem(system: string): number {
    // System counts should be based on selected teams + repositories
    const filteredData = this.getDataFilteredByTeamsAndRepos();
    return filteredData
      .filter(point => point.systems?.includes(system))
      .reduce((sum, point) => sum + point.alertCount, 0);
  }

  getAlertCountForSeverity(severity: string): number {
    // Severity counts should be based on selected teams + repositories + alert types
    const filteredData = this.getDataFilteredByTeamsReposAndAlerts();
    return filteredData
      .filter(point => point.severity === severity)
      .reduce((sum, point) => sum + point.alertCount, 0);
  }

  getAlertCountForLocation(location: string): number {
    // Location is not part of cascading hierarchy, use cached count
    return this.alertCountCache.locations.get(location) || 0;
  }

  getAlertCountForPipelineType(pipelineType: string): number {
    // Pipeline type is not part of cascading hierarchy, use cached count
    return this.alertCountCache.pipelineTypes.get(pipelineType) || 0;
  }

  getAlertCountForAdmin(admin: string): number {
    // Admin is not part of cascading hierarchy, use cached count
    return this.alertCountCache.admins.get(admin) || 0;
  }

  // Efficient cache population method
  private populateAlertCountCache(): void {
    // Clear existing cache
    this.alertCountCache.teams.clear();
    this.alertCountCache.repositories.clear();
    this.alertCountCache.alertTypes.clear();
    this.alertCountCache.locations.clear();
    this.alertCountCache.pipelineTypes.clear();
    this.alertCountCache.admins.clear();
    this.alertCountCache.systems.clear();
    this.alertCountCache.severities.clear();

    // Populate teams cache (sum of all repositories owned by team)
    const teams = new Set(this.chartDataPoints.map(d => d.teamName));
    teams.forEach(team => {
      const count = this.chartDataPoints
        .filter(point => point.teamName === team)
        .reduce((sum, point) => sum + point.alertCount, 0);
      this.alertCountCache.teams.set(team, count);
    });

    // Populate repositories cache (now we have real repository-level data)
    const repositories = new Set(this.chartDataPoints.map(d => d.repository).filter(Boolean));
    repositories.forEach(repo => {
      const count = this.chartDataPoints
        .filter(point => point.repository === repo)
        .reduce((sum, point) => sum + point.alertCount, 0);
      this.alertCountCache.repositories.set(repo!, count);
    });

    // Populate alert types cache
    const alertTypes = new Set(this.chartDataPoints.map(d => d.alertName));
    alertTypes.forEach(alertType => {
      const count = this.chartDataPoints
        .filter(point => point.alertName === alertType)
        .reduce((sum, point) => sum + point.alertCount, 0);
      this.alertCountCache.alertTypes.set(alertType, count);
    });

    // Populate systems cache
    const systems = new Set(this.chartDataPoints.flatMap(d => d.systems || []));
    systems.forEach(system => {
      const count = this.chartDataPoints
        .filter(point => point.systems?.includes(system))
        .reduce((sum, point) => sum + point.alertCount, 0);
      this.alertCountCache.systems.set(system, count);
    });

    // Populate severities cache
    const severities = new Set(this.chartDataPoints.map(d => d.severity).filter(Boolean));
    severities.forEach(severity => {
      const count = this.chartDataPoints
        .filter(point => point.severity === severity)
        .reduce((sum, point) => sum + point.alertCount, 0);
      this.alertCountCache.severities.set(severity!, count);
    });

    // Populate other caches similarly
    const locations = new Set(this.chartDataPoints.map(d => d.location).filter(Boolean));
    locations.forEach(location => {
      const count = this.chartDataPoints
        .filter(point => point.location === location)
        .reduce((sum, point) => sum + point.alertCount, 0);
      this.alertCountCache.locations.set(location!, count);
    });

    const pipelineTypes = new Set(this.chartDataPoints.map(d => d.pipelineType).filter(Boolean));
    pipelineTypes.forEach(pipelineType => {
      const count = this.chartDataPoints
        .filter(point => point.pipelineType === pipelineType)
        .reduce((sum, point) => sum + point.alertCount, 0);
      this.alertCountCache.pipelineTypes.set(pipelineType!, count);
    });

    const admins = new Set(this.chartDataPoints.map(d => d.admin).filter(Boolean));
    admins.forEach(admin => {
      const count = this.chartDataPoints
        .filter(point => point.admin === admin)
        .reduce((sum, point) => sum + point.alertCount, 0);
      this.alertCountCache.admins.set(admin!, count);
    });
  }
}
